import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Gauge,
  Buildings,
  Package,
  ClipboardText,
  Cube,
  ListChecks,
  MagnifyingGlass,
  Users,
  Gear,
  SignOut,
  Stack,
  ChartLine,
  Factory,
  UserCircle,
  Wrench,
  CalendarBlank,
  ClockCounterClockwise,
  ChartBar,
  Truck,
  Receipt,
} from "@phosphor-icons/react";

const NAV_SECTIONS = [
  {
    section: null, // no header for top-level items
    items: [
      { to: "/", label: "Dashboard", icon: Gauge, end: true },
    ],
  },
  {
    section: "Route Card",
    items: [
      { to: "/suppliers", label: "Suppliers", icon: Buildings },
      { to: "/parts", label: "Parts", icon: Package },
      { to: "/grns", label: "GRNs", icon: ClipboardText },
      { to: "/assembly-runs", label: "Assembly Runs", icon: Factory },
      { to: "/inventory", label: "Inventory", icon: Stack },
      { to: "/boxes", label: "Boxes", icon: Cube },
      { to: "/packing-lists", label: "Packing Lists", icon: ListChecks },
      { to: "/dispatches", label: "Dispatches", icon: Truck },
    ],
  },
  {
    section: "Daily Production",
    items: [
      { to: "/staff", label: "Staff", icon: UserCircle },
      { to: "/operations", label: "Operations", icon: Wrench },
      { to: "/schedule", label: "Schedule", icon: CalendarBlank },
      { to: "/timesheets", label: "Timesheets", icon: ClockCounterClockwise },
      { to: "/weekly-snapshot", label: "Payroll Snapshot", icon: Receipt },
      { to: "/workforce-reports", label: "Workforce", icon: ChartBar },
    ],
  },
  {
    section: "Insights",
    items: [
      { to: "/reports", label: "Reports", icon: ChartLine },
      { to: "/trace", label: "Traceability", icon: MagnifyingGlass },
    ],
  },
];

// Flat list retained for the header-title lookup
const NAV = NAV_SECTIONS.flatMap((s) => s.items);

const ADMIN_NAV = [
  { to: "/users", label: "Users", icon: Users, role: "admin" },
  { to: "/settings", label: "Settings", icon: Gear, role: "admin" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const flat = [...NAV, ...ADMIN_NAV.filter((n) => !n.role || user?.role === n.role)];
  const adminItems = ADMIN_NAV.filter((n) => !n.role || user?.role === n.role);

  return (
    <div className="min-h-screen bg-white flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r-2 border-[#334155] bg-white sticky top-0 h-screen flex flex-col" data-testid="sidebar">
        <div className="px-5 py-4 border-b-2 border-[#334155] bg-[#1e293b] text-white flex items-center gap-3">
          <img
            src="/logo-gate.jpg"
            alt="GATE"
            className="w-12 h-12 brut-border border-white shrink-0 object-cover"
            data-testid="brand-logo"
          />
          <div className="min-w-0">
            <div className="font-display text-base tracking-tight uppercase leading-tight">US Operations</div>
            <div className="text-[10px] font-mono-ui opacity-70">v1.0 · Route Card</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {NAV_SECTIONS.map((sec, si) => (
            <div key={si} className={sec.section ? "mt-3" : ""}>
              {sec.section && (
                <div className="px-5 pt-3 pb-1 text-[10px] font-display uppercase tracking-[0.25em] text-slate-400 border-t border-slate-200" data-testid={`nav-section-${sec.section.toLowerCase().replace(/\s+/g, "-")}`}>
                  {sec.section}
                </div>
              )}
              {sec.items.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2.5 border-l-4 border-transparent font-display uppercase text-sm tracking-wider ${
                        isActive
                          ? "border-l-[#1d4ed8] bg-[#eff6ff] text-[#1e293b]"
                          : "text-[#334155] hover:bg-slate-50"
                      }`
                    }
                  >
                    <Icon size={18} weight="bold" />
                    {it.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
          {adminItems.length > 0 && (
            <div className="mt-3">
              <div className="px-5 pt-3 pb-1 text-[10px] font-display uppercase tracking-[0.25em] text-slate-400 border-t border-slate-200">
                Admin
              </div>
              {adminItems.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2.5 border-l-4 border-transparent font-display uppercase text-sm tracking-wider ${
                        isActive
                          ? "border-l-[#1d4ed8] bg-[#eff6ff] text-[#1e293b]"
                          : "text-[#334155] hover:bg-slate-50"
                      }`
                    }
                  >
                    <Icon size={18} weight="bold" />
                    {it.label}
                  </NavLink>
                );
              })}
            </div>
          )}
        </nav>

        <div className="border-t-2 border-[#334155] px-5 py-4">
          <div className="brut-tag brut-tag-yellow mb-2" data-testid="role-badge">{user?.role}</div>
          <div className="text-sm font-bold truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-slate-600 truncate mb-3" data-testid="user-email">{user?.email}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="brut-btn w-full px-3 py-2 text-xs inline-flex items-center justify-center gap-2"
          >
            <SignOut size={14} weight="bold" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 bg-white">
        <div className="px-8 py-6 border-b-2 border-[#334155] bg-white sticky top-0 z-10 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display tracking-[0.3em] text-slate-500">// CONTROL PANEL</div>
            <div className="font-display text-xl uppercase tracking-tight">
              {flat.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)))?.label || "Dashboard"}
            </div>
          </div>
          <div className="brut-tag brut-tag-dark font-mono-ui" data-testid="header-clock">
            {new Date().toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}
          </div>
        </div>
        <div className="p-8 brut-grid-bg min-h-[calc(100vh-90px)]">{children}</div>
      </main>
    </div>
  );
}
